import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAssessmentResults, deleteAssessmentResult } from "../services/assessmentSlice";
import {
  updateAssessmentResult,
  resetUpdateState,
} from "../services/updateAssessmentSlice";
import logo from "../image/depLogo.png"
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Button,
  Divider,
  Autocomplete,
  TextField
} from "@mui/material";
import PrintIcon from '@mui/icons-material/Print'; 

import AssessmentDialog from "./AssessmentDialog";
import AssessmentCard from "./AssessmentCard";
import { MasonryLayout } from "../utils/layouts";
import { RATING_OPTIONS } from "./ratingOptions"; 

const AssessmentCards = () => {
  const dispatch = useDispatch();

  const { results, loading, error } = useSelector((state) => state.assessment);
  const { status: updateStatus, error: updateError } = useSelector(
    (state) => state.updateAssessment
  );

  const [descriptions, setDescriptions] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);

  // حالات الفلاتر
  const [filterStatus, setFilterStatus] = useState("all"); 
  const [filterStudent, setFilterStudent] = useState(null); // يفضل أن يكون null للاوتوكومبليت
  const [filterLevel, setFilterLevel] = useState("");

  const [teacherName, setTeacherName] = useState("");
  const [schoolName, setSchoolName] = useState("");

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setTeacherName(parsedUser?.fullName || "غير متوفر");
        setSchoolName(parsedUser?.school?.schoolName || "غير متوفر");
      }
    } catch (err) {
      console.error("Error reading from localStorage", err);
    }
  }, []);

  useEffect(() => {
    dispatch(fetchAssessmentResults({ page: 1, perPage: 10000 }));
  }, [dispatch]);

  useEffect(() => {
    if (updateStatus === "succeeded" || updateStatus === "failed") {
      setSnackbarOpen(true);
      if (updateStatus === "succeeded") {
        setDialogOpen(false);
      }
    }
  }, [updateStatus, dispatch]);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    dispatch(resetUpdateState());
  };

  const handleOpenDialog = (id, level) => {
    setSelectedCardId(id);
    setSelectedLevel(level);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCardId(null);
    setSelectedLevel(null);
  };

  const handleSaveDescription = (newDescriptionText) => {
    setDescriptions((prev) => ({
      ...prev,
      [selectedCardId]: newDescriptionText,
    }));

    dispatch(
      updateAssessmentResult({
        id: selectedCardId,
        resultText: newDescriptionText,
      })
    );
  };

  const handleDeleteAssessment = (id) => {
    dispatch(deleteAssessmentResult(id));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getRatingIcon = (levelValue) => {
    const option = RATING_OPTIONS.find(opt => String(opt.value) === String(levelValue) || opt.label === levelValue);
    
    if (option && option.icon) {
      const IconComponent = option.icon;
      return <IconComponent size={20} color={option.color} />;
    }
    return levelValue; 
  };

  const uniqueStudents = useMemo(() => {
    if (!results) return [];
    return [...new Set(results.map(item => item?.student?.full_name || item.studentName).filter(Boolean))];
  }, [results]);

  const counts = useMemo(() => {
    if (!results) return { all: 0, unassigned: 0, assigned: 0 };
    return {
      all: results.length,
      unassigned: results.filter(item => item.level === (descriptions[item.id] || item.result)).length,
      assigned: results.filter(item => item.level !== (descriptions[item.id] || item.result)).length,
    };
  }, [results, descriptions]);

  // حساب العدادات لكل تقييم (للأيقونات)
  const ratingCounts = useMemo(() => {
    const countsMap = {};
    RATING_OPTIONS.forEach(opt => {
      countsMap[opt.value] = 0;
    });

    if (results && results.length > 0) {
      results.forEach(item => {
        if (item.level) {
          countsMap[item.level] = (countsMap[item.level] || 0) + 1;
        }
      });
    }
    return countsMap;
  }, [results]);

  const filteredResults = useMemo(() => {
    if (!results) return [];

    return results.filter((item) => {
      const currentDescription = descriptions[item.id] || item.result;
      let matchesStatus = true;
      if (filterStatus === "unassigned") {
        matchesStatus = item.level === currentDescription;
      } else if (filterStatus === "assigned") {
        matchesStatus = item.level !== currentDescription;
      }

      const studentName = item?.student?.full_name || item.studentName;
      const matchesStudent = filterStudent ? studentName === filterStudent : true;
      const matchesLevel = filterLevel ? String(item.level) === String(filterLevel) : true;

      return matchesStatus && matchesStudent && matchesLevel;
    });
  }, [results, filterStatus, filterStudent, filterLevel, descriptions]);

  const handlePrint = () => {
    window.print();
  };

  // تنسيق مشترك للأزرار 
  const toggleButtonStyles = {
    px: 2, 
    py: 1, 
    fontWeight: 'bold',
    display: 'flex', 
    alignItems: 'center', 
    gap: 1,
    border: '1px solid #cbd5e1 !important',
    borderRadius: '8px !important',
    margin: '0 !important',
    bgcolor: 'white',
    whiteSpace: 'nowrap',
    '&.Mui-selected': {
      bgcolor: '#e2e8f0', 
      borderColor: '#94a3b8 !important',
      color: 'text.primary'
    },
    '&:hover': {
      bgcolor: '#f1f5f9',
    }
  };

  if (loading && (!results || results.length === 0)) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ m: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>

      {/* ========================================================= */}
      {/* 1. واجهة العرض العادية                                    */}
      {/* ========================================================= */}
      <Box sx={{ '@media print': { display: 'none' } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", color: "primary.main" }}>
            نتائج التقييم الوصفي
          </Typography>
          
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<PrintIcon />} 
            onClick={handlePrint}
            disabled={filteredResults.length === 0}
          >
            طباعة القائمة
          </Button>
        </Box>

        {/* شريط الفلاتر الموحد */}
        {results && results.length > 0 && (
          <Paper 
            elevation={0} 
            sx={{ 
              p: 1.5, 
              mb: 4, 
              borderRadius: 2, 
              backgroundColor: '#f8fafc', 
              border: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              overflowX: 'auto', // تمرير أفقي
              '&::-webkit-scrollbar': { height: '6px' },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: '10px' }
            }}
          >
            
            {/* 1. أزرار حالة التعيين */}
            <ToggleButtonGroup
              value={filterStatus}
              exclusive
              onChange={(e, newValue) => {
                if (newValue !== null) setFilterStatus(newValue);
              }}
              aria-label="Assessment Status"
              sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}
            >
              <ToggleButton value="all" sx={toggleButtonStyles}>
                الكل ({counts.all})
              </ToggleButton>
              <ToggleButton value="assigned" sx={toggleButtonStyles}>
                تم التعيين ({counts.assigned})
              </ToggleButton>
              <ToggleButton value="unassigned" sx={toggleButtonStyles}>
                لم يتم التعيين ({counts.unassigned})
              </ToggleButton>
            </ToggleButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ borderColor: '#cbd5e1', my: 1 }} />

            {/* 2. أزرار التقييمات بالأيقونات */}
            <ToggleButtonGroup
              value={filterLevel}
              exclusive
              onChange={(e, newValue) => {
                setFilterLevel(newValue !== null ? newValue : "");
              }}
              aria-label="Filter by Rating"
              sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}
            >
              <ToggleButton value="" sx={toggleButtonStyles}>
                كل التقييمات
              </ToggleButton>

              {RATING_OPTIONS.map((option) => {
                const IconComponent = option.icon;
                const count = ratingCounts[option.value] || 0;
                
                return (
                  <ToggleButton 
                    key={option.value} 
                    value={String(option.value)} 
                    sx={toggleButtonStyles}
                    title={option.label}
                  >
                    {IconComponent && <IconComponent size={22} color={count > 0 ? option.color : '#b0bec5'} />}
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '15px',
                        color: count > 0 ? 'text.primary' : 'text.disabled' 
                      }}
                    >
                      ({count})
                    </Typography>
                  </ToggleButton>
                );
              })}
            </ToggleButtonGroup>

            <Divider orientation="vertical" flexItem sx={{ borderColor: '#cbd5e1', my: 1 }} />

            {/* 3. فلتر الطالب (Autocomplete للبحث الذكي) */}
            <Autocomplete
              size="small"
              options={uniqueStudents}
              value={filterStudent}
              onChange={(event, newValue) => {
                setFilterStudent(newValue);
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="بحث عن طالب..." 
                  variant="outlined" 
                  sx={{ 
                    bgcolor: 'white',
                    borderRadius: '8px',
                    '& fieldset': { borderColor: '#cbd5e1', borderRadius: '8px' }
                  }}
                />
              )}
              sx={{ minWidth: 240, flexShrink: 0 }}
              noOptionsText="لا يوجد طلاب"
            />

          </Paper>
        )}

        {filteredResults.length === 0 ? (
           <Typography align="center" color="text.secondary" sx={{ mt: 5 }}>لا توجد نتائج مطابقة للبحث الحالي.</Typography>
        ) : (
          <MasonryLayout>
            {filteredResults.map((item) => (
              <AssessmentCard
                key={item.id}
                item={item}
                description={descriptions[item.id]}
                onOpenDialog={handleOpenDialog}
                onDelete={handleDeleteAssessment}
              />
            ))}
          </MasonryLayout>
        )}
      </Box>

      {/* ========================================================= */}
      {/* 2. واجهة الطباعة المُحسّنة                                 */}
      {/* ========================================================= */}
      <Box 
        sx={{ 
          display: 'none', 
          '@media print': { 
            display: 'block',
            direction: 'rtl',
            margin: '0',
            width: '100%',
          } 
        }}
      >
        <style type="text/css" media="print">
          {`
            @page { 
              size: A4 landscape; 
              margin: 10mm 15mm 15mm 15mm; 
            }
            body { 
              -webkit-print-color-adjust: exact !important; 
              print-color-adjust: exact !important; 
              background-color: white;
            }

            ::-webkit-scrollbar { display: none; }
            
            .print-header {
              border-bottom: 2px solid #1976d2;
              padding-bottom: 12px;
              margin-bottom: 20px;
            }

            .print-table { 
              width: 100%; 
              border-collapse: collapse; 
              page-break-inside: auto; 
            }
            .print-table tr { 
              page-break-inside: avoid; 
              page-break-after: auto; 
            }
            .print-table thead { 
              display: table-header-group; 
            }
            .print-table tfoot { 
              display: table-footer-group; 
            }
            
            .print-table thead th, .print-table tbody td {
              border: 1px solid #555 !important;
              padding: 8px 6px !important;
              font-size: 13px !important;
              vertical-align: middle;
            }
            .print-table thead th {
              background-color: #e3f2fd !important; 
              color: #000 !important;
              font-weight: bold;
              font-size: 14px !important;
              text-align: center;
            }

            .page-footer-print {
              position: fixed;
              bottom: 0;
              left: 0;
              width: 100%;
              display: flex;
              justify-content: flex-start;
              align-items: center;
              gap: 12px;
              padding: 5px 25px;
              background-color: white; 
              opacity: 0.65;
            }

            .footer-space {
              height: 55px; 
            }
          `}
        </style>

        <Box className="print-header">
          <Typography variant="h5" align="center" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
            تقرير التقييمات الوصفية
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, mt: 2 }}>
            <Typography sx={{ fontWeight: 'bold', fontSize: '16px', color: '#333' }}>
              المدرسة: <span style={{fontWeight: 'normal'}}>{schoolName}</span>
            </Typography>
            <Typography sx={{ fontWeight: 'bold', fontSize: '16px', color: '#333' }}>
              المعلم: <span style={{fontWeight: 'normal'}}>{teacherName}</span>
            </Typography>
          </Box>
        </Box>

        <table className="print-table">
          <thead>
            <tr>
              <th style={{ width: '4%' }}>ت</th>
              <th style={{ width: '14%', textAlign: 'right' }}>اسم الطالب</th>
              <th style={{ width: '9%' }}>التاريخ</th>
              <th style={{ width: '10%' }}>الصف والشعبة</th>
              <th style={{ width: '14%', textAlign: 'right' }}>عنوان الدرس</th>
              <th style={{ width: '15%', textAlign: 'right' }}>محور التقييم</th>
              <th style={{ width: '5%' }}>التقييم</th>
              <th style={{ width: '29%', textAlign: 'right' }}>العبارة الوصفية</th>
            </tr>
          </thead>
          
          <tbody>
            {filteredResults.map((item, index) => {
              const currentDescription = descriptions[item.id] || item.result;
              const isUnassigned = item.level === currentDescription;

              return (
                <tr key={item.id}>
                  <td style={{ textAlign: 'center' }}>{index + 1}</td>
                  <td style={{ textAlign: 'right' }}>{item?.student?.full_name}</td>
                  <td style={{ textAlign: 'center' }} dir="ltr">{formatDate(item?.time)}</td>
                  <td style={{ textAlign: 'center' }}>{item?.class?.fullTitle}</td>
                  <td style={{ textAlign: 'right' }}>{item?.lessonTitle}</td>
                  <td style={{ textAlign: 'right' }}>{item?.assessmentCriteria?.title}</td>
                  <td style={{ textAlign: 'center' }}>{getRatingIcon(item?.level)}</td>
                  <td style={{ textAlign: 'right', lineHeight: '1.6' }}>
                    {isUnassigned ? "لم يتم التعيين بعد" : currentDescription}
                  </td>
                </tr>
              );
            })}
          </tbody>

          <tfoot>
            <tr>
              <td colSpan={8} style={{ border: 'none !important', padding: '0 !important' }}>
                <div className="footer-space"></div>
              </td>
            </tr>
          </tfoot>
        </table>

        <div className="page-footer-print">
          <img 
            src={logo} 
            alt="logo" 
            style={{ 
              width: '45px', 
              height: '45px', 
              objectFit: 'contain' 
            }} 
          />
          <Typography sx={{ fontWeight: 'bold', fontSize: '15px', color: '#0b807b' }}>
            مجموعة مدارس العميد التعليمية
          </Typography>
        </div>

      </Box>

      {/* ===== مكونات أخرى (Dialog & Snackbar) ===== */}
      <AssessmentDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveDescription}
        isLoading={updateStatus === "loading"}
        itemId={selectedCardId}
        level={selectedLevel}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={updateStatus === "succeeded" ? "success" : "error"}
          sx={{ width: "100%" }}
        >
          {updateStatus === "succeeded" ? "تم حفظ الوصف بنجاح" : updateError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AssessmentCards;